import { SearchDefinitionModel, FieldState, FilterFieldDefinition, OptionState } from '../../../models';
import { createSortCallback } from '@anecdotes/utils';
import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { FilterDefinitionStoreService } from '../../../services';
import { DataManipulationSelectors } from '../../../store/selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-data-filter-field',
  templateUrl: './data-filter-field.component.html',
  styleUrls: ['./data-filter-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('parent', [transition(':enter', [])]),
    // To prevent initial animation
    // https://stackoverflow.com/questions/44111239/angular-is-there-a-way-to-skip-enter-animation-oninitial-render
    trigger('expandField', [
      transition(':enter', [
        style({ height: '0' }), // initial
        animate('0.2s', style({ height: '*' })), // final
      ]),
      transition(':leave', [
        style({ height: '*' }), // initial
        animate('0.2s', style({ height: '0' })), // final
      ]),
    ]),
  ],
  standalone: false,
})
export class DataFilterFieldComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input()
  fieldId: string;

  @Input()
  filterInstanceId: string;

  @Input()
  expandable = true;

  field: FieldState;
  allOptions: OptionState[];
  collapsableOptions: OptionState[] = [];
  staticOptions: OptionState[] = [];
  filteredOptions: OptionState[] = [];
  filteredOptionsToDisplay: OptionState[] = [];
  isSearchFieldExist: boolean;
  isSearchMode: boolean;
  fixedOptionsCount: number;
  searchDefinitions: SearchDefinitionModel<OptionState>[] = [
    {
      propertyId: 'id',
      propertySelector: (option): string => option.id,
    },
  ];

  get isCollapsingSupported(): boolean {
    return this.field && this.allOptions && this.allOptions.length > this.field.maxVisibleOptions;
  }

  get isExpanded(): boolean {
    return !this.isCollapsingSupported || !!this.collapsableOptions.length;
  }

  get optionCount(): number {
    return this.allOptions.filter((option) => !!option).length;
  }

  get fieldEmpty(): boolean {
    return !this.allOptions?.length;
  }

  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
    private filterDefinitionStoreService: FilterDefinitionStoreService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(DataManipulationSelectors.Filter.createFieldStateSelector(this.filterInstanceId, this.fieldId))),
      this.store.pipe(
        select(DataManipulationSelectors.Filter.createFieldRelatedOptionsSelector(this.filterInstanceId, this.fieldId))
      ),
      this.filterDefinitionStoreService.getFilterFieldDefinition(this.filterInstanceId, this.fieldId),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([field, options, filterFieldDefinition]) => {
        this.field = field;
        this.isSearchFieldExist = filterFieldDefinition.isSearchEnabled;
        this.allOptions = this.handleOptionsSorting(options, filterFieldDefinition);
        this.filteredOptions = [...this.allOptions];
        this.filteredOptionsToDisplay = [...this.filteredOptions];
        this.fixedOptionsCount = filterFieldDefinition?.fixedOptions?.length ?? 0;

        if (this.isExpanded) {
          this.showAllOptions();
        } else {
          this.hideMaxVisibleOptions();
        }

        this.cd.detectChanges();
      });
  }

  isAddSeparationLine(index: number): boolean {
    const displayedOptionsCount = this.allOptions.filter((option) => option?.isDisplayed).length;

    return (index + 1 === this.fixedOptionsCount && displayedOptionsCount > this.fixedOptionsCount);
  }

  setFoundOptions(options: OptionState[]): void {
    this.filteredOptionsToDisplay = options;
  }

  setDefaultOptions(event: InputEvent): void {
    this.isSearchMode = !!(event.target as HTMLInputElement)?.value.length;

    if (!this.isSearchMode) {
      this.filteredOptionsToDisplay = [...this.filteredOptions];
      this.hideMaxVisibleOptions();
    }
  }

  toggleShowMoreState(): void {
    if (this.isExpanded) {
      this.hideMaxVisibleOptions();
    } else {
      this.showAllOptions();
    }

    this.cd.detectChanges();
  }

  optionTrackBy(index: number, option: OptionState): string {
    return option?.id || index.toString();
  }

  resolveShowAllButtonTranslationKey(): string {
    return this.isExpanded ? 'showLessOptionsButtonText' : 'showMoreOptionsButtonText';
  }

  buildTranslationKey(relativeKey: string): string {
    return `core.dataFilter.${relativeKey}`;
  }

  private handleOptionsSorting(
    optionsToSort: OptionState[],
    filterFieldDefinition: FilterFieldDefinition<any>
  ): OptionState[] {
    let optionsToSortCopy = [...optionsToSort];

    if (filterFieldDefinition.customSortCallback) {
      if (filterFieldDefinition.sortWithFixedOptions) {
        optionsToSortCopy = [...optionsToSortCopy].sort((option1, option2) => filterFieldDefinition.customSortCallback(option1.value, option2.value));
      } else {
        const notFixedOptions = optionsToSortCopy.filter((option: OptionState) => !option.isFixed)
          .sort((option1, option2) => filterFieldDefinition.customSortCallback(option1.value, option2.value));

        const fixedOptions = optionsToSortCopy.filter((option) => option.isFixed);

        optionsToSortCopy = [...fixedOptions,...notFixedOptions];
      }
    } else if (filterFieldDefinition.useSort) {
      if (filterFieldDefinition.fixedOptions) {
        const valuesDictionary: { [key: string]: OptionState } = optionsToSortCopy.reduce((result, opt) => {
          result[opt.value] = opt;
          return result;
        }, {});
        optionsToSortCopy = [];

        if (!filterFieldDefinition.sortWithFixedOptions) {
          filterFieldDefinition.fixedOptions.forEach((opt) => {
            optionsToSortCopy.push(valuesDictionary[opt.value]);
            delete valuesDictionary[opt.value];
          });
        }

        optionsToSortCopy.push(...this.sortAscending(Object.values(valuesDictionary)));
      }
    }
    return optionsToSortCopy;
  }

  private getPropertyToSortBy(option: OptionState): string {
    return option.translationKey ? this.translateService.instant(option.translationKey) : option.value;
  }

  private sortAscending(optionsToSort: OptionState[]): OptionState[] {
    const optionsToSortCopy = [...optionsToSort];

    return optionsToSortCopy.sort(
      createSortCallback((opt) => this.getPropertyToSortBy(opt).toLocaleLowerCase(), 'ASC')
    );
  }

  private hideMaxVisibleOptions(): void {
    if (this.isCollapsingSupported) {
      this.staticOptions = this.allOptions.slice(0, this.field.maxVisibleOptions);
      this.collapsableOptions = [];
    } else {
      this.staticOptions = this.allOptions;
    }
  }

  private showAllOptions(): void {
    if (this.isCollapsingSupported) {
      this.staticOptions = this.allOptions.slice(0, this.field.maxVisibleOptions);
      this.collapsableOptions = this.allOptions.slice(this.field.maxVisibleOptions);
    } else {
      this.staticOptions = this.allOptions;
      this.collapsableOptions = [];
    }
  }
}
